// Unified brand fit lookup (Build 3d).
//
// Combines direct verdict + curated similarity + measurement similarity
// into a single function consumers can call to get the strongest available
// brand fit signal for a given retailer.
//
// IMPORTANT: This file MUST stay byte-identical to
// `supabase/functions/_shared/brandFitLookup.ts`. Edge functions cannot
// import from `src/lib`, so the module is duplicated.

import { normaliseBrand } from "./brandRegistry";
import {
  findAnchorsForBrand,
  isUnreliableBrand,
  type FitPole,
  type SimilarityConfidence,
} from "./brandSimilarity";
import { findMeasurementSimilarBrands } from "./brandSimilarityMeasured";

export type BrandFitVerdict = "true_to_size" | "runs_small" | "runs_large";

export interface UserBrandFitEntry {
  brand: string; // canonical brand name
  verdict: BrandFitVerdict;
  category: "tops" | "bottoms";
  fit_reliability?: "high" | "medium" | "low";
}

export type BrandFitSource =
  | "direct"
  | "curated_similar"
  | "measured_similar"
  | "both_similar"
  | null;

export interface BrandFitLookupResult {
  has_signal: boolean;
  verdict: BrandFitVerdict | null;
  source: BrandFitSource;
  anchor_brand: string | null;
  confidence: "low" | "medium" | "high" | null;
  conflict_detected: boolean;
  reasoning: string;
}

interface InternalMatch {
  anchor: string;
  verdict: BrandFitVerdict;
  fit_pole: FitPole | null;
  confidence: SimilarityConfidence;
  source: "curated" | "measured";
}

export async function lookupBrandFit(
  retailer: string,
  user_verdicts: UserBrandFitEntry[],
  gender: "women" | "men" | "non-binary",
  category: "tops" | "bottoms",
  // deno-lint-ignore no-explicit-any
  supabase?: any,
): Promise<BrandFitLookupResult> {
  // Step 0: Normalise retailer
  const canonical_retailer = normaliseBrand(retailer);
  if (!canonical_retailer) {
    return emptyResult("Retailer could not be identified");
  }

  // Step 0b: Skip unreliable brands entirely
  if (isUnreliableBrand(canonical_retailer)) {
    return emptyResult("Brand has inconsistent sizing — cannot infer fit");
  }

  // Step 1: Direct verdict match
  const direct = user_verdicts.find(
    (v) => v.brand === canonical_retailer && v.category === category,
  );

  if (direct) {
    return {
      has_signal: true,
      verdict: direct.verdict,
      source: "direct",
      anchor_brand: canonical_retailer,
      confidence: direct.fit_reliability === "high" ? "high" : "medium",
      conflict_detected: false,
      reasoning: `You said ${canonical_retailer} ${verdictPhrase(direct.verdict)}`,
    };
  }

  // Step 2: Curated similarity — find anchors that user has verdicts for
  const curated_anchors = findAnchorsForBrand(canonical_retailer, gender, category);

  const user_curated_matches: InternalMatch[] = curated_anchors
    .filter((anchor) =>
      user_verdicts.some((v) => v.brand === anchor.anchor && v.category === category),
    )
    .map((anchor) => {
      const verdict_entry = user_verdicts.find(
        (v) => v.brand === anchor.anchor && v.category === category,
      )!;
      return {
        anchor: anchor.anchor,
        verdict: verdict_entry.verdict,
        fit_pole: anchor.fit,
        confidence: anchor.confidence,
        source: "curated" as const,
      };
    });

  // Step 3: Measurement similarity (if data available)
  const measurement_matches: InternalMatch[] = [];

  if (supabase && category === "tops") {
    try {
      const aggregates = await loadAggregatesIfAvailable(supabase, category);
      if (aggregates.length > 0) {
        const measured = findMeasurementSimilarBrands(
          canonical_retailer,
          category,
          gender,
          aggregates,
        );

        for (const m of measured.similar_brands) {
          const user_verdict = user_verdicts.find(
            (v) => v.brand === m.brand && v.category === category,
          );
          if (user_verdict) {
            measurement_matches.push({
              anchor: m.brand,
              verdict: user_verdict.verdict,
              fit_pole: null,
              confidence: m.confidence,
              source: "measured" as const,
            });
          }
        }
      }
    } catch (e) {
      console.warn("[brand-fit-lookup] measurement lookup failed:", e);
    }
  }

  const all_matches = [...user_curated_matches, ...measurement_matches];

  if (all_matches.length === 0) {
    return emptyResult("No brand fit data available for this retailer");
  }

  // Step 4: Multi-match handling (C with B fallback)
  const curated_poles = user_curated_matches
    .map((m) => m.fit_pole)
    .filter((p): p is FitPole => p !== null);

  const unique_poles = new Set(curated_poles);

  if (unique_poles.size > 1) {
    return {
      has_signal: false,
      verdict: null,
      source: null,
      anchor_brand: null,
      confidence: null,
      conflict_detected: true,
      reasoning:
        "Your brand fits suggest mixed signals for this retailer — check size guide",
    };
  }

  const best = pickBestMatch(all_matches);

  const has_curated = user_curated_matches.length > 0;
  const has_measured = measurement_matches.length > 0;
  const source: BrandFitSource =
    has_curated && has_measured
      ? "both_similar"
      : has_curated
        ? "curated_similar"
        : has_measured
          ? "measured_similar"
          : null;

  return {
    has_signal: true,
    verdict: best.verdict,
    source,
    anchor_brand: best.anchor,
    confidence: best.confidence,
    conflict_detected: false,
    reasoning: buildReasoning(canonical_retailer, best, source),
  };
}

// Helpers

function emptyResult(reason: string): BrandFitLookupResult {
  return {
    has_signal: false,
    verdict: null,
    source: null,
    anchor_brand: null,
    confidence: null,
    conflict_detected: false,
    reasoning: reason,
  };
}

function verdictPhrase(v: BrandFitVerdict): string {
  switch (v) {
    case "true_to_size":
      return "fits true to size for you";
    case "runs_small":
      return "runs small — you size up";
    case "runs_large":
      return "runs large — you size down";
  }
}

function pickBestMatch(matches: InternalMatch[]): InternalMatch {
  const high = matches.filter((m) => m.confidence === "high");
  if (high.length > 0) return high[0];

  const medium = matches.filter((m) => m.confidence === "medium");
  if (medium.length > 0) return medium[0];

  return matches[0];
}

function buildReasoning(
  retailer: string,
  match: InternalMatch,
  source: BrandFitSource,
): string {
  const phrase = verdictPhrase(match.verdict);

  if (source === "curated_similar") {
    return `${retailer} fits similarly to ${match.anchor} — ${phrase}`;
  }
  if (source === "measured_similar") {
    return `${retailer}'s measurements match ${match.anchor} — ${phrase}`;
  }
  if (source === "both_similar") {
    return `${retailer} matches ${match.anchor} on both style and measurements — ${phrase}`;
  }
  return phrase;
}

// deno-lint-ignore no-explicit-any
async function loadAggregatesIfAvailable(supabase: any, category: string) {
  const { data, error } = await supabase
    .from("brand_measurement_aggregates")
    .select("*")
    .eq("category", category);
  if (error) return [];
  return data || [];
}
